import type { FacetFilters, SearchParams } from 'algoliasearch'

import { getAlgoliaClient } from './client'

export interface AlgoliaHitsRequest {
  indexName: string
  query: string
  hitsPerPage: number
  userToken: string | undefined
  facetFilters?: FacetFilters
}

export interface AlgoliaTrendingRequest {
  indexName: string
  /** Minimum confidence score a recommendation must reach to be returned. */
  threshold: number
  maxItems: number
  facets: { facetName: string, maxRecommendations: number }[]
  /** Narrows the trending items only */
  facetFilters?: FacetFilters
}

export const buildFacetFilter = (
  facetName: string,
  values: string[]
): FacetFilters | undefined =>
  values.length ? [values.map((value) => `${facetName}:${value}`)] : undefined

/**
 * Single-index search returning raw hits. `THit` describes the shape of a record
 * in the queried index - mapping it onto anything else is the caller's job.
 */
export const algoliaSearchSingleIndex = async <THit>(
  indexName: string,
  searchParams: SearchParams
) => {
  const { search } = getAlgoliaClient()

  if (!search) return null

  return search.searchSingleIndex<THit>({
    indexName,
    searchParams: {
      ...searchParams,
      clickAnalytics: true
    }
  })
}

/**
 * Multi-index search issued as a single Algolia request - results come back in the
 * order the requests were given. Combining the queries avoids doubled requests billing.
 */
export const algoliaSearchForHits = async (requests: AlgoliaHitsRequest[]) => {
  const { search } = getAlgoliaClient()

  if (!search) return null

  return search.searchForHits({
    requests: requests.map((request) => ({
      ...request,
      page: 0,
      clickAnalytics: true
    }))
  })
}

/**
 * Trending items plus trending values of the given facets, in one Recommend request.
 * Results come back as [items, ...facets] following the requested facets order.
 */
export const algoliaGetTrendingItemsAndFacets = async ({
  indexName,
  threshold,
  maxItems,
  facets,
  facetFilters
}: AlgoliaTrendingRequest) => {
  const { recommend } = getAlgoliaClient()

  if (!recommend) return null

  return recommend.getRecommendations({
    requests: [
      {
        indexName,
        model: 'trending-items',
        threshold,
        maxRecommendations: maxItems,
        ...(facetFilters
          ? { queryParameters: { facetFilters }, fallbackParameters: { facetFilters } }
          : {})
      },
      ...facets.map(({ facetName, maxRecommendations }) => ({
        indexName,
        model: 'trending-facets' as const,
        facetName,
        threshold,
        maxRecommendations
      }))
    ]
  })
}
